import { debugLog } from '@/shared/utils/debug-log';

type CachedJSONPayload<T> = { etag: string; value: T };

// Resource data is a disposable, immutable tab cache, not persistent storage.
// Avoid JSON parse/stringify and synchronous Web Storage on every bed save.
const resourceCache = new Map<string, CachedJSONPayload<unknown>>();
const retiredStorageKeys = new Set<string>();
const appDataCachePrefix = 'blink-hoh:app-data:';

function removeLegacyCache(cacheKey: string) {
  try { window.sessionStorage.removeItem(appDataCachePrefix + cacheKey); } catch { /* Storage may be unavailable. */ }
}

export function readCachedJSON<T>(cacheKey: string): CachedJSONPayload<T> | null {
  if (!retiredStorageKeys.has(cacheKey)) {
    removeLegacyCache(cacheKey);
    retiredStorageKeys.add(cacheKey);
  }
  return resourceCache.get(cacheKey) as CachedJSONPayload<T> | undefined ?? null;
}

export function writeCachedJSON<T>(cacheKey: string, value: T, etag: string | null) {
  if (!etag) {
    resourceCache.delete(cacheKey);
    return;
  }
  resourceCache.set(cacheKey, {etag, value});
  debugLog('api/cache', 'cache-write', {cacheKey, etag});
}

export function getCachedETag(cacheKey: string) {
  return readCachedJSON<unknown>(cacheKey)?.etag ?? null;
}

export function clearCachedJSON(cacheKeys: readonly string[]) {
  for (const cacheKey of cacheKeys) {
    resourceCache.delete(cacheKey);
    removeLegacyCache(cacheKey);
    retiredStorageKeys.delete(cacheKey);
  }
}
